import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';

const CognitiveProfileCard = ({ cognitiveProfile, expanded = false }) => {
  if (!cognitiveProfile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No cognitive data available</p>
      </motion.div>
    );
  }

  const { learningStyle, strengths, weaknesses, timeManagement, problemSolvingApproach, knowledgeRetention } = cognitiveProfile;

  // Prepare radar chart data with safe defaults
  const radarData = [
    { subject: 'Time Mgmt', value: 100 - (timeManagement?.rushingTendency || 0), fullMark: 100 },
    { subject: 'Methodical', value: problemSolvingApproach?.methodical || 0, fullMark: 100 },
    { subject: 'Intuitive', value: problemSolvingApproach?.intuitive || 0, fullMark: 100 },
    { subject: 'Short-term', value: knowledgeRetention?.shortTerm || 0, fullMark: 100 },
    { subject: 'Mid-term', value: knowledgeRetention?.midTerm || 0, fullMark: 100 },
    { subject: 'Long-term', value: knowledgeRetention?.longTerm || 0, fullMark: 100 }
  ];

  const getLearningStyleColor = (style) => {
    switch (style) {
      case 'visual': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'analytical': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'mixed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className="p-3 bg-purple-600 rounded-lg"
          >
            <Brain className="h-6 w-6 text-white" />
          </motion.div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Cognitive Profile</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Your learning patterns</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLearningStyleColor(learningStyle)}`}>
          {learningStyle?.charAt(0).toUpperCase() + learningStyle?.slice(1)} Learner
        </span>
      </div>

      {/* Radar Chart */}
      <div className="mb-6">
        <ResponsiveContainer width="100%" height={250}>
          <RadarChart data={radarData}>
            <PolarGrid stroke="#cbd5e1" />
            <PolarAngleAxis dataKey="subject" tick={{ fill: '#64748b', fontSize: 12 }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#64748b' }} />
            <Radar
              name="Cognitive Skills"
              dataKey="value"
              stroke="#8b5cf6"
              fill="#8b5cf6"
              fillOpacity={0.6}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Strengths & Weaknesses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Strengths */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-600" />
            Top Strengths
          </h4>
          <div className="space-y-2">
            {strengths.slice(0, 3).map((strength, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-700 rounded-lg p-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{strength.topic}</span>
                  <span className="text-xs font-medium text-green-600">{strength.score}%</span>
                </div>
                <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${strength.score}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                    className="h-full bg-gradient-to-r from-green-400 to-green-600"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Weaknesses */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-600" />
            Areas to Improve
          </h4>
          <div className="space-y-2">
            {weaknesses.slice(0, 3).map((weakness, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-700 rounded-lg p-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{weakness.topic}</span>
                  <span className="text-xs font-medium text-red-600">{weakness.score}%</span>
                </div>
                <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${weakness.score}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                    className="h-full bg-gradient-to-r from-red-400 to-red-600"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Knowledge Retention */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600"
        >
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Knowledge Retention
          </h4>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Short-term</p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-1">(0-7 days)</p>
              <p className="text-2xl font-bold text-blue-600">{knowledgeRetention?.shortTerm || 0}%</p>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Mid-term</p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-1">(8-29 days)</p>
              <p className="text-2xl font-bold text-indigo-600">{knowledgeRetention?.midTerm || 0}%</p>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Long-term</p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mb-1">(30+ days)</p>
              <p className="text-2xl font-bold text-purple-600">{knowledgeRetention?.longTerm || 0}%</p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CognitiveProfileCard;
